import React, { useEffect } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { onboardStyles } from '../components/ComponentStyles';
import SkipConfirmation from '../components/SkipConfirmation';
import HelpModal from '../components/onboarding/HelpModal';
import OnboardErrorAlert from '../components/onboarding/OnboardErrorAlert';
import OnboardFooter from '../components/onboarding/OnboardFooter';
// Import new components
import OnboardHeader from '../components/onboarding/OnboardHeader';
import TSPLoginFactory from '../components/onboarding/TSPLoginFactory';
import { useAppState } from '../contexts/AppStateProvider';
import { useJWT } from '../contexts/JWTProvider';
import useOAuth from '../hooks/useOAuth';
import useOnboardForm from '../hooks/useOnboardForm';
import useSkipOnboarding from '../hooks/useSkipOnboarding';
import { ServiceThemeOptions } from '../themes';
import {
  getTSPHelpTextByKey,
  getTSPImageNameByKey,
  getTSPNameByKey,
} from '../tsps';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { handleOnboardError } from '../utils/onboardErrorHandling';
import { SlackTracker, createSlackContext } from '../utils/slackNotifications';

export default function Onboard() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;

  // Extract theme values
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const textPrimaryColor = theme.palette?.text?.primary || '#000000';
  const textSecondaryColor = theme.palette?.text?.secondary || '#586166';

  // Use new centralized hooks
  const jwt = useJWT();
  const {
    approved,
    magicLink,
    motiveClientID,
    samsaraClientID,
    zubieClientID,
    bouncieClientID,
    TSP,
    updatingTSP,
    serviceName,
    redirectURIError,
    redirectURISuccess,
    setOnboarded,
    setRedirectURI,
    setTSP,
    setTspCount,
    tspCount,
  } = useAppState();

  // Get JWT-derived properties
  const token = jwt.rawToken || '';
  const isTokenValid = jwt.isValid;
  const fleetCode = jwt.fleetCode || '';
  const serviceId = jwt.serviceId;

  // Help modal state
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const handleHelpClick = React.useCallback(() => {
    setHelpModalOpen(true);
  }, [setHelpModalOpen]);

  // Use the skip onboarding hook (moved before useOnboardForm to avoid dependency issues)
  const {
    skipDialogOpen,
    handleSkipClick,
    handleSkipConfirmed,
    handleCloseSkipDialog,
  } = useSkipOnboarding({
    token,
    redirectURIError,
    redirectURISuccess,
    serviceName,
    tsp: TSP,
  });

  // Use the onboard form hook
  const {
    isFormValid,
    setIsFormValid,
    disableButton,
    showError,
    errorText,
    errorDetails,
    setShowError,
    setErrorText,
    linkState,
    handleFormSubmit,
  } = useOnboardForm({
    TSP,
    serviceName,
    token,
    fleetCode,
    updatingTSP,
    serviceID: String(serviceId || ''),
    magicLink,
    redirectURISuccess,
    clientIDs: {
      motiveClientID,
      samsaraClientID,
      zubieClientID,
      bouncieClientID,
    },
    onSuccess: () => {
      setOnboarded(true);
      navigate('/success');
    },
    setRedirectURI,
    setTspCount,
    currentTspCount: tspCount, // Pass current TSP count for incrementing
    onSkip: handleSkipConfirmed, // Use the skip handler from useSkipOnboarding
  });

  // Use the OAuth redirects hook
  const oAuthHandlers = useOAuth({
    serviceName,
    linkState,
    clientIDs: {
      motiveClientID,
      samsaraClientID,
      zubieClientID,
      bouncieClientID,
    },
    onError: (error) => {
      setShowError(true);
      setErrorText(error);
    },
  });

  // Validate token and approval on mount.
  useEffect(() => {
    if (!isTokenValid) {
      // Track error in Slack
      const slackContext = createSlackContext(jwt, serviceName);
      SlackTracker.errorEncountered(
        slackContext,
        'invalid_token',
        'Token validation failed on onboard page',
        'Onboard'
      );

      handleOnboardError(
        { message: 'Invalid token', response: { status: 401 } },
        { tsp: TSP, serviceName },
        { navigate, setShowError, setErrorText, search }
      );
    } else if (!approved) {
      navigate('/');
    } else {
      // Track page view
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        tsp: TSP,
        redirectURISuccess,
        redirectURIError,
      });
      Analytics.pageView(analyticsContext, 'Onboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTokenValid,
    approved,
    navigate,
    TSP,
    serviceName,
    search,
    setShowError,
    setErrorText,
    // Include missing dependencies
    redirectURIError,
    redirectURISuccess,
    // Include specific JWT properties used for analytics
    jwt.serviceId,
    jwt.fleetCode,
    jwt.rawToken,
  ]); // Performance optimized: specific JWT properties included for analytics context

  // Handle back button click.
  const handleBackClick = () => {
    // Check if user came from updateOrAdd page
    const urlParams = new URLSearchParams(search);
    const fromPage = urlParams.get('from');

    setTSP('');

    if (fromPage === 'updateOrAdd') {
      navigate('/updateOrAdd', { replace: true });
    } else {
      navigate('/selectTSP', { replace: true });
    }
  };

  // Get TSP info
  const TSPName = getTSPNameByKey(TSP) || '';
  const TSPHelpText = getTSPHelpTextByKey(TSP);
  const TSPImageName = getTSPImageNameByKey(TSP) || '';

  return (
    <>
      <Box sx={onboardStyles.mainContainer}>
        <Paper
          elevation={3}
          sx={{
            ...onboardStyles.paper,
            bgcolor: paperBgColor,
          }}
        >
          {/* Header with back button and logo */}
          <OnboardHeader
            onBackClick={handleBackClick}
            imageName={TSPImageName}
            tsp={TSP}
            serviceName={serviceName}
            backgroundColor={paperBgColor}
          />

          {/* Title and subtitle */}
          <Box sx={onboardStyles.titleContainer}>
            <Typography
              variant='h5'
              align='center'
              sx={{
                ...onboardStyles.title(theme as any),
                color: textPrimaryColor,
              }}
            >
              Add your {TSPName} account
            </Typography>

            <Typography
              variant='body2'
              align='center'
              sx={{
                ...onboardStyles.subtitle(theme as any),
                color: textSecondaryColor,
              }}
            >
              By providing your {TSPName} credentials to Axle, you're enabling
              Axle to retrieve your telematics data.
            </Typography>
          </Box>

          {/* Form area */}
          <FormContainer
            defaultValues={{
              username: '',
              password: '',
              database: '',
              apikey: '',
              authorizationcode: '',
              carriername: '',
              FTPUsername: '',
              FTPPassword: '',
              skipCredentials: false,
            }}
            onSuccess={handleFormSubmit}
            FormProps={{
              autoComplete: 'off',
              id: 'onboarding-form',
            }}
          >
            <Box sx={onboardStyles.formContainer}>
              {showError && (
                <OnboardErrorAlert
                  errorText={errorText}
                  helpText={errorDetails?.helpText}
                  showEscapeHatch={errorDetails?.showEscapeHatch}
                  tsp={TSP}
                  serviceName={serviceName}
                  onClose={() => setShowError(false)}
                  onSkip={handleSkipClick}
                  onHelpClick={handleHelpClick}
                />
              )}

              <Box sx={onboardStyles.loginFormContainer}>
                <TSPLoginFactory
                  tsp={TSP}
                  onValidityChange={setIsFormValid}
                  TSP={TSP}
                  serviceName={serviceName}
                  onHelpClick={handleHelpClick}
                  oAuthHandlers={oAuthHandlers}
                />
              </Box>
            </Box>
          </FormContainer>

          {/* Footer with buttons */}
          <OnboardFooter
            isFormValid={isFormValid}
            disableButton={disableButton}
            tsp={TSP}
            primaryColor={primaryColor}
            onHelpClick={handleHelpClick}
          />
        </Paper>
      </Box>

      {/* Help Modal */}
      <HelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        helpText={TSPHelpText}
        tsp={TSP}
        serviceName={serviceName}
      />

      {/* Skip Confirmation Dialog */}
      <SkipConfirmation
        open={skipDialogOpen}
        onClose={handleCloseSkipDialog}
        onSkip={handleSkipConfirmed}
        serviceName={serviceName}
      />
    </>
  );
}
